import React, { createContext, useContext, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';

// Toast types
const TOAST_TYPES = {
  success: 'success',
  error: 'error',
  warning: 'warning',
  info: 'info'
};

// Create the context
const ToastContext = createContext(null);

/**
 * Toast Provider Component
 * Provides toast notification functionality to the entire app
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  // Generate unique ID for each toast
  const generateId = useCallback(() => `toast-${Date.now()}-${Math.floor(Math.random() * 1000)}`, []);

  // Add a new toast to the stack.
  // `action` is optional: { label, onClick } renders a button beside the
  // message, for toasts the user is meant to act on rather than just read
  // (the "new version available" prompt). Pass duration 0 to make it persist.
  const showToast = useCallback((type, message, duration = 5000, action = null) => {
    if (!Object.values(TOAST_TYPES).includes(type)) {
      console.warn(`Invalid toast type: ${type}`);
      type = TOAST_TYPES.info; // Default to info
    }

    const newToast = {
      id: generateId(),
      type,
      message,
      duration,
      action
    };

    setToasts(prev => [...prev, newToast]);

    // Auto dismiss after duration
    if (duration > 0) {
      setTimeout(() => {
        dismissToast(newToast.id);
      }, duration);
    }

    return newToast.id;
  }, [generateId]);

  // Remove a toast by ID
  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const contextValue = {
    toasts,
    showToast,
    dismissToast,
    TOAST_TYPES
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {createPortal(
        <ToastContainer toasts={toasts} dismissToast={dismissToast} />,
        document.body
      )}
    </ToastContext.Provider>
  );
};

/**
 * Toast Container Component
 * Renders the toast notifications
 * 
 * @param {Object} props
 * @param {Array} props.toasts - Array of toast objects
 * @param {Function} props.dismissToast - Function to dismiss a toast
 */
const ToastContainer = ({ toasts, dismissToast }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[10001] flex flex-col gap-2">
      {[...toasts].reverse().map(toast => (
        <Toast key={toast.id} toast={toast} onDismiss={dismissToast} />
      ))}
    </div>
  );
};

/**
 * Individual Toast Component
 * 
 * @param {Object} props
 * @param {Object} props.toast - Toast data
 * @param {Function} props.onDismiss - Function to dismiss this toast
 */
const TOAST_CONFIG = {
  success: { styles: 'bg-green-100 border-green-500 text-green-700', Icon: CheckCircle2 },
  error:   { styles: 'bg-red-100 border-red-500 text-red-700',       Icon: XCircle },
  warning: { styles: 'bg-yellow-100 border-yellow-500 text-yellow-700', Icon: AlertTriangle },
  info:    { styles: 'bg-blue-100 border-blue-500 text-blue-700',    Icon: Info },
};

const Toast = ({ toast, onDismiss }) => {
  const { id, type, message, action } = toast;
  const { styles, Icon } = TOAST_CONFIG[type] ?? TOAST_CONFIG.info;

  return (
    <div
      className={`max-w-sm w-full shadow-lg rounded-lg pointer-events-auto overflow-hidden border-l-4 ${styles}`}
      role="alert"
    >
      <div className="px-3 py-2 flex items-center gap-2">
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <p className="text-sm font-medium flex-grow">{message}</p>
        {action && (
          <button
            type="button"
            className="text-xs font-semibold underline underline-offset-2 shrink-0 hover:opacity-70 focus:outline-none"
            onClick={() => action.onClick()}
          >
            {action.label}
          </button>
        )}
        <button
          type="button"
          className="text-current opacity-50 hover:opacity-100 focus:outline-none shrink-0"
          onClick={() => onDismiss(id)}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

/**
 * Custom hook to use the toast context
 * @returns {Object} Toast context value
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastContext;